/**
 * useAIKeys Hook
 *
 * React hook for managing AI provider API keys.
 * Wraps AIKeyApiService with React state management.
 *
 * @module ai-settings/hooks
 */

import { useState, useEffect, useCallback } from 'react';
import type { AIProviderType, AIProvidersStatus } from '../types/aiKey.types';
import { DEFAULT_PROVIDERS_STATUS } from '../types/aiKey.types';
import { aiKeyApiService } from '../services/AIKeyApiService';
import { clearAvailableAIModelsCache } from './useAvailableAIModels';

export interface UseAIKeysResult {
  /** Provider status map (configured/not, lastValidated) */
  providers: AIProvidersStatus;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Saving state */
  saving: boolean;
  /** Validating state */
  validating: boolean;

  /** Store a key (validates first, then stores in Secret Manager) */
  storeKey: (provider: AIProviderType, apiKey: string) => Promise<void>;
  /** Delete a key */
  deleteKey: (provider: AIProviderType) => Promise<void>;
  /** Validate a key without storing */
  validateKey: (provider: AIProviderType, apiKey: string) => Promise<boolean>;
  /** Refresh provider status */
  refresh: () => Promise<void>;
}

export function useAIKeys(projectId: string | undefined): UseAIKeysResult {
  const [providers, setProviders] = useState<AIProvidersStatus>(DEFAULT_PROVIDERS_STATUS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const status = await aiKeyApiService.listKeys(projectId);
      setProviders(status);
    } catch (err: any) {
      setError(err.message || 'Failed to load AI key settings');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const storeKey = useCallback(async (provider: AIProviderType, apiKey: string) => {
    if (!projectId) throw new Error('No project selected');
    try {
      setSaving(true);
      setError(null);
      await aiKeyApiService.storeKey(projectId, provider, apiKey);
      clearAvailableAIModelsCache(projectId);
      await refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to store key');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [projectId, refresh]);

  const deleteKey = useCallback(async (provider: AIProviderType) => {
    if (!projectId) throw new Error('No project selected');
    try {
      setSaving(true);
      setError(null);
      await aiKeyApiService.deleteKey(projectId, provider);
      clearAvailableAIModelsCache(projectId);
      await refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to delete key');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [projectId, refresh]);

  const validateKey = useCallback(async (provider: AIProviderType, apiKey: string) => {
    if (!projectId) throw new Error('No project selected');
    try {
      setValidating(true);
      setError(null);
      return await aiKeyApiService.validateKey(provider, apiKey, projectId);
    } catch (err: any) {
      setError(err.message || 'Validation failed');
      return false;
    } finally {
      setValidating(false);
    }
  }, [projectId]);

  return {
    providers,
    loading,
    error,
    saving,
    validating,
    storeKey,
    deleteKey,
    validateKey,
    refresh,
  };
}
